import { RouteObject } from "react-router";
import { Redirect } from "src/router/helper/Redirect";
import { EstimateModalRoutes } from "src/router/routes/jobs/EstimateModals";

const GroupsChildren: RouteObject[] = [
  {
    path: "",
    async loader(args) {
      const { estimateOverviewLoader } = await import("src/router/loaders/jobs/Estimates");
      return estimateOverviewLoader(args);
    },
    shouldRevalidate({ currentUrl, nextUrl, defaultShouldRevalidate }) {
      if (currentUrl.pathname === nextUrl.pathname) return false;
      return defaultShouldRevalidate;
    },
    async lazy() {
      const { EstimateOverview } = await import("jobs/Job/EstimatesTab/EstimateEditor/EstimateOverview");
      return { element: <EstimateOverview /> };
    },
    children: EstimateModalRoutes,
  },
  {
    path: ":groupTabIndex",
    async loader(args) {
      const { estimateGroupTabLoader } = await import("src/router/loaders/jobs/Estimates");
      return estimateGroupTabLoader(args);
    },
    shouldRevalidate({ currentUrl, nextUrl, defaultShouldRevalidate }) {
      if (currentUrl.pathname === nextUrl.pathname) {
        return false;
      }
      return defaultShouldRevalidate;
    },
    children: EstimateModalRoutes,
    async lazy() {
      const { EstimateGroupTab } = await import("jobs/Job/EstimatesTab/EstimateEditor/EstimateGroupTab");
      return { element: <EstimateGroupTab /> };
    },
  },
];

const EstimateEditorChildren: RouteObject[] = [
  {
    path: "groups",
    children: GroupsChildren,
  },
  { path: "", element: <Redirect to="groups" /> },
  {
    path: "finance_application",
    async loader(args) {
      const { oneLookLocationLoader } = await import(
        /* webpackChunkName: "jobs-cache-one-look-location-loader" */ "src/router/loaders/jobs/Estimates"
      );
      return oneLookLocationLoader(args);
    },
    async lazy() {
      const { FinanceApplicationModal } = await import(
        /* webpackChunkName: "jobs-cache-finance-application-modal" */ "jobs/Job/EstimatesTab/OneLook/FinanceApplicationModal"
      );
      return { element: <FinanceApplicationModal /> };
    },
  },
  {
    path: "present",
    async loader(args) {
      const { enhancedPricePresentationLoader } = await import(
        /* webpackChunkName: "jobs-cache-enhanced-price-presentation-loader" */ "src/router/loaders/jobs/Estimates"
      );
      return enhancedPricePresentationLoader(args);
    },
    shouldRevalidate({ currentUrl, nextUrl, defaultShouldRevalidate }) {
      // Slide navigation only changes the `?slide=` search (same pathname), so skip
      // re-running the loader (and re-fetching 1LOOK locations) on every arrow click.
      if (currentUrl.pathname === nextUrl.pathname) return false;
      return defaultShouldRevalidate;
    },
    async lazy() {
      const { EnhancedPricePresentationModal } = await import(
        /* webpackChunkName: "jobs-cache-enhanced-price-presentation-modal" */ "jobs/Job/EstimatesTab/EnhancedPricePresentationModal"
      );
      return { element: <EnhancedPricePresentationModal /> };
    },
  },
  {
    path: "ingage",
    async loader(args) {
      const { ingagePricePresentationLoader } = await import(
        /* webpackChunkName: "jobs-cache-ingage-price-presentation-loader" */ "src/router/loaders/jobs/Estimates"
      );
      return ingagePricePresentationLoader(args);
    },
    async lazy() {
      const { IngagePricePresentationModal } = await import(
        /* webpackChunkName: "jobs-cache-ingage-price-presentation-modal" */ "jobs/Job/EstimatesTab/IngagePricePresentation"
      );
      return { element: <IngagePricePresentationModal /> };
    },
  },
  {
    path: "add-template",
    async loader(args) {
      const { estimateTemplatePickerLoader } = await import(
        /* webpackChunkName: "jobs-cache-estimate-template-picker-loader" */ "src/router/loaders/jobs/Estimates"
      );
      return estimateTemplatePickerLoader(args);
    },
    async lazy() {
      const { EstimateTemplatePicker } = await import(
        /* webpackChunkName: "jobs-cache-estimate-template-picker" */ "jobs/Job/EstimatesTab/EstimateEditor/EstimateTemplatePicker"
      );
      return { element: <EstimateTemplatePicker /> };
    },
  },
  {
    path: "save-template",
    async loader(args) {
      const { saveEstimateTemplateLoader } = await import(
        /* webpackChunkName: "jobs-cache-save-estimate-template-loader" */ "src/router/loaders/jobs/Estimates"
      );
      return saveEstimateTemplateLoader(args);
    },
    async lazy() {
      const { SaveEstimateTemplate } = await import(
        /* webpackChunkName: "jobs-cache-save-estimate-template" */ "jobs/Job/EstimatesTab/EstimateEditor/SaveEstimateTemplate"
      );
      return { element: <SaveEstimateTemplate /> };
    },
  },
  {
    path: "manage-templates",
    async loader(args) {
      const { manageTemplatesLoader } = await import(
        /* webpackChunkName: "jobs-cache-manage-templates-loader" */ "src/router/loaders/jobs/Estimates"
      );
      return manageTemplatesLoader(args);
    },
    async lazy() {
      const { EstimateTemplateManager } = await import(
        /* webpackChunkName: "jobs-cache-estimate-template-manager" */ "jobs/Job/EstimatesTab/EstimateEditor/EstimateTemplateManager"
      );
      return { element: <EstimateTemplateManager /> };
    },
  },
  {
    path: "compare",
    async loader(args) {
      const { priceComparisonLoader } = await import(
        /* webpackChunkName: "jobs-cache-price-comparison-loader" */ "src/router/loaders/jobs/Estimates"
      );
      return priceComparisonLoader(args);
    },
    shouldRevalidate({ currentUrl, nextUrl, defaultShouldRevalidate }) {
      if (currentUrl.pathname === nextUrl.pathname) return false;
      return defaultShouldRevalidate;
    },
    async lazy() {
      const { PriceComparison } = await import(
        /* webpackChunkName: "jobs-cache-price-comparison" */ "jobs/Job/EstimatesTab/PriceComparison"
      );
      return { element: <PriceComparison /> };
    },
  },
];

export const EstimatesRoutes: RouteObject[] = [
  {
    path: "new",
    async loader(args) {
      const { estimateEditorLoader } = await import("src/router/loaders/jobs/Estimates");
      return estimateEditorLoader(args);
    },
    shouldRevalidate({ currentUrl, nextUrl }) {
      // Only revalidate when navigating to the new estimate route for the first time,
      // not when moving between its child routes (group tabs, overview, etc.).
      return !currentUrl.pathname.includes("/estimates/new");
    },
    async lazy() {
      const { EstimateEditor } = await import("jobs/Job/EstimatesTab/EstimateEditor");
      return { element: <EstimateEditor /> };
    },
    children: EstimateEditorChildren,
  },
  {
    path: ":estimateId",
    async loader(args) {
      const { estimateEditorLoader } = await import("src/router/loaders/jobs/Estimates");
      return estimateEditorLoader(args);
    },
    shouldRevalidate({ currentParams, nextParams, defaultShouldRevalidate }) {
      // Don't revalidate when navigating within the same estimate (e.g., between group tabs,
      // to the overview, or when the line item editor search param changes).
      if (currentParams.estimateId === nextParams.estimateId) {
        return false;
      }
      return defaultShouldRevalidate;
    },
    async lazy() {
      const { EstimateEditor } = await import("jobs/Job/EstimatesTab/EstimateEditor");
      return { element: <EstimateEditor /> };
    },
    children: EstimateEditorChildren,
  },
  {
    path: "",
    async loader(args) {
      const { estimatesLoader } = await import("src/router/loaders/jobs/Estimates");
      return estimatesLoader(args);
    },
    // Opening/closing the Commission Calculator modal keeps the estimates listing mounted behind it —
    // don't re-run the listing loader for that toggle (it re-lists and can redirect to a sticky current
    // estimate, eating the modal navigation). Only suppress the modal open/close toggle itself; all
    // other navigations (including listing pagination/search param changes) keep default revalidation.
    shouldRevalidate({ currentUrl, nextUrl, defaultShouldRevalidate }) {
      const isCommissionModal = (pathname: string) => /\/commission\/[^/]+$/.test(pathname);
      const listBase = (pathname: string) => pathname.replace(/\/commission\/[^/]+$/, "");
      const togglingModal = isCommissionModal(currentUrl.pathname) !== isCommissionModal(nextUrl.pathname);
      if (togglingModal && listBase(currentUrl.pathname) === listBase(nextUrl.pathname)) return false;
      return defaultShouldRevalidate;
    },
    async lazy() {
      const { EstimatesTab } = await import("jobs/Job/EstimatesTab");
      return { element: <EstimatesTab /> };
    },
    children: [
      {
        path: "commission/:estimateId",
        async loader(args) {
          const { commissionCalculatorLoader } = await import("src/router/loaders/jobs/Estimates");
          return commissionCalculatorLoader(args);
        },
        // Reload only if the modal is pointed at a different estimate (future-proofs an in-modal
        // switch); today the modal unmounts on close so it's a fresh match each open.
        shouldRevalidate: ({ currentParams, nextParams }) => currentParams.estimateId !== nextParams.estimateId,
        async lazy() {
          const { CommissionCalculatorModal } = await import(
            /* webpackChunkName: "jobs-cache-commission-calculator-modal" */ "jobs/components/EstimatesList/CommissionCalculator/CommissionCalculatorModal"
          );
          return { element: <CommissionCalculatorModal /> };
        },
      },
    ],
  },
];
